import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Brush, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import styles from './styles.module.scss';
import { ReactComponent as TravellerIcon } from './assets/TravellerIcon.svg';

import {
  ChartColorsMap,
  ChartDeepOptions,
  ChartDefaultOption,
  ChartDotRadius,
  ChartHeightMobile,
  ChartSoilHeight,
  ChartYAxisWidth,
} from './constants/index.jsx';
import { BreakpointMobile } from '../../constants/ui';
import { useViewport } from '../../hooks';
import { ChartLegendSoil, ChartNav, ChartSoilTooltip } from './index.jsx';
import { GetBrushPosition, GetSoilCsvKeys, MakeTicks } from './lib/index.jsx';
import { downloadCSV } from '../../utils/charts';
import { DateTimeFormat } from '../../constants/data';

const ChartSoil = ({ t, period, data, count, deep, chartsMap, sensors, chartId }) => {
  const { widthViewport } = useViewport();

  const [csvKeys, setCsvKeys] = useState('');
  const [brushIndex, setBrushIndex] = useState([0, 0]);
  const [domainYAsis, setDomainYAsis] = useState(null);
  const [ticksYAsis, setTicksYAsis] = useState(null);
  const [selectedDeep, setSelectedDeep] = useState([ChartDefaultOption]);

  const onChangeDeep = (values) => {
    setSelectedDeep(values);
  };

  useEffect(() => {
    if (!data || data.length === 0 || !count || !deep) return;

    setSelectedDeep(deep);

    // Update brush position
    setBrushIndex(GetBrushPosition({ data, period }));

    // Ticks
    if (chartId === chartsMap.soilTemperature.id || chartId === chartsMap.salinity.id) {
      const values = data.flatMap((item) =>
        {
          if (!item) return;
          return ChartDeepOptions.map((d) => item[`value${d.value}`]).filter((value) => value !== null && value !== undefined)
        }
      );
      const maxValue = Math.max(...values);
      const minValue = Math.min(...values);
      setTicksYAsis(MakeTicks({ value: maxValue, count: 8, stepBase: 1 }));
      setDomainYAsis([minValue, maxValue]);
    } else {
      setDomainYAsis([0, 100]);
    }
    // CSV keys
    setCsvKeys(GetSoilCsvKeys(data[0]));
  }, [period, data]);

  return (
    <div className={styles.chartContainer}>
      {/*Legend*/}
      <ChartLegendSoil
        t={t}
        title={chartsMap[chartId].title}
        units={chartsMap[chartId].units}
        data={selectedDeep}
        onDownloadCsv={() =>
          downloadCSV({
            data: (data || []).map((item) => ({ ...item, date: `"${dayjs(item.date).format(DateTimeFormat)}"` })),
            keys: csvKeys,
            fileName: `cropler_${sensors[0]}_${chartsMap[chartId].csvFileName}`,
          })
        }
      />

      {data && data.length > 0 ? (
        <div className={styles.chartContainerRow}>
          {/*Chart*/}
          <ResponsiveContainer
            width="100%"
            height={widthViewport > BreakpointMobile ? ChartSoilHeight : ChartHeightMobile}
          >
            <LineChart
              className={'rechart-soil'}
              width={800}
              height={widthViewport > BreakpointMobile ? ChartSoilHeight : ChartHeightMobile}
              data={data ? [...data] : []}
              margin={{ top: 2, right: 28 }}
              syncId={`${chartId}_id`}
            >
              <CartesianGrid strokeDasharray="0 0" stroke={ChartColorsMap.grid} vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                minTickGap={60}
                height={50}
                tickSize={12}
                tickFormatter={(value) => dayjs(value).format('MMM DD')}
              />
              <YAxis
                yAxisId="left"
                width={ChartYAxisWidth}
                tickCount={11}
                tickSize={0}
                tickMargin={10}
                {...(domainYAsis && { domain: domainYAsis })}
                {...(ticksYAsis && { ticks: ticksYAsis })}
              />
              <Tooltip content={<ChartSoilTooltip selectedDeep={selectedDeep} units={chartsMap[chartId].units} />} />
              {ChartDeepOptions.map((obj) => (
                <Line
                  key={obj.value}
                  className={`${selectedDeep.find((el) => el.value === obj.value) ? 'show-line-chart' : ''} line-chart`}
                  yAxisId="left"
                  type="monotone"
                  dataKey={`value${obj.value}`}
                  stroke={obj.color}
                  strokeWidth={2}
                  activeDot={{
                    r: ChartDotRadius,
                    fill: selectedDeep.find((el) => el.value === obj.value) ? '#fff' : 'transparent',
                    stroke: selectedDeep.find((el) => el.value === obj.value) ? obj.color : 'transparent',
                  }}
                />
              ))}
              <Brush
                height={40}
                stroke={ChartColorsMap.brush}
                dataKey={'date'}
                tickFormatter={(value) => dayjs(value).format('MMM DD')}
                startIndex={brushIndex[0]}
                endIndex={brushIndex[1]}
                alwaysShowText={true}
                traveller={<TravellerIcon />}
                travellerWidth={14}
              />
            </LineChart>
          </ResponsiveContainer>

          <ChartNav count={count ? count : null} selectedDeep={deep} onChangeDeep={onChangeDeep} />
        </div>
      ) : (
        <div>{t('empty.noData')}</div>
      )}
    </div>
  );
};

export default ChartSoil;
